import React, { lazy, useEffect, useState } from "react";
import AuthNavBar from "../../components/navbars/AuthNavBar";
import MobileNav from "../../components/navbars/MobileNav";
import { Container } from "reactstrap";
import logo from "../../assets/img/temp_logo.png";
import hexRgb from "hex-rgb";
import { utcToZonedTime } from "date-fns-tz";
import { format } from "date-fns";
import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";
import _ from "lodash";
import { sendAnalytics, updateEventAmount } from "../../helpers";
import axios from "axios";

axios.defaults.baseURL = `${process.env.REACT_APP_BE_URL}/api`;
axios.defaults.withCredentials = true;
const sessionTimeDelay = process.env.REACT_APP_SESSION_TIME_POLLING_DELAY

/*const VideoHero = lazy( () => import( '../../components/sections/VideoHero' ) );*/
const HeroVertical = lazy(() =>
  import("../../components/sections/HeroVertical")
);
const BigIdea = lazy(() => import("../../components/sections/BigIdea"));
const Product = lazy(() => import("../../components/sections/Product"));
const ProductExpanded = lazy(() =>
  import("../../components/sections/ProductExpanded")
);
const CompetitorLandscape = lazy(() =>
  import("../../components/sections/CompetitorLandscape")
);
const CarouselCards = lazy(() =>
  import("../../components/sections/CarouselCards")
);
const Cards = lazy(() => import("../../components/sections/Cards"));
const TeamMembers = lazy(() => import("../../components/sections/TeamMembers"));
const FinalCTA = lazy(() => import("../../components/sections/FinalCTA"));
/*const ImageHero = lazy( () => import( '../../components/sections/ImageHero' ) );*/
const Projections = lazy(() => import("../../components/sections/Projections"));
const Investments = lazy(() => import("../../components/sections/Investments"));
const BusinessModel = lazy(() =>
  import("../../components/sections/BusinessModel")
);
const BusinessModelCarousel = lazy(() =>
  import("../../components/sections/BusinessModelCarousel")
);
const Research = lazy(() => import("../../components/sections/Research"));
const Downloads = lazy(() => import("../../components/sections/Downloads"));
const TeamMembersCarousel = lazy(() =>
  import("../../components/sections/TeamMembersCarousel")
);
const ImageProjection = lazy(() =>
  import("../../components/sections/ImageProjection")
);

const BasicTemplate = (props) => {
  const { page } = props;
  const [sectionsViewed, setSectionsViewed] = useState([]);
  const [sessionTime, setSessionTime] = useState(0);
  const [sessionTimeEventId, setSessionTimeEventId] = useState(null);
  const [pageViewed, setPageViewed] = useState(false)
  const [fullPageWasViewed, setFullPageWasViewed] = useState(false)
  const totalNumberOfSections =
    page?.sections && Array.isArray(page.sections) && page.sections.length;
  const search = useLocation().search;
  const investorId = new URLSearchParams(search).get("investor_id");

  useEffect(() => {
    const sendPageViewEvent = () => {
      if (!pageViewed) {
        sendAnalytics({
          event: "PAGE_VIEWED",
          event_type: "SESSION",
          event_amount: 1,
          event_date: new Date(),
          pitch_page_id: page.pitch_page_id,
          contact_id: investorId,
        })
       setPageViewed(true)
      }
    }
    
    const sendInitialSessionTime = () => {
      // Wait 3s to track so we know it's an intentional view
      const timer = setTimeout(async () => {
        // If user navigates to page, then away, don't continue to track
        if (!document.hidden) {
          const response = await sendAnalytics({
            event: "SESSION_TIME",
            event_type: "SESSION",
            event_amount: 3,
            event_date: new Date(),
            pitch_page_id: page?.pitch_page_id,
            contact_id: investorId,
          })  
          setSessionTime(3)
          setSessionTimeEventId(response?.data?.newEvent?.id)
          sendPageViewEvent()
        }
      }, 3000)
      return timer
    }
    
    // Account for user navigating away without closing the tab
    function navigatedAway() {
      if (!document.hidden) {
        sendInitialSessionTime()
        sendPageViewEvent()
      } else {
        setSessionTime(0)
        setSessionTimeEventId(null)
        setPageViewed(false)
        setSectionsViewed([])
        setFullPageWasViewed(false)
      }
    }

    // Send initial data and create listener for case when user leaves
    // Also, remove the event listener and timer when finished
    const timer = sendInitialSessionTime()
    document.addEventListener('visibilitychange', () => navigatedAway())
    return () => {
      clearTimeout(timer)
      document.removeEventListener('visibilitychange', navigatedAway())
    }
  }, [])

  useEffect(() => {
    const interval = setInterval(() => {
      setSessionTime(prevTime => prevTime + 30)
    }, sessionTimeDelay);

    return () => clearInterval(interval);
  }, [sessionTimeEventId]);

  useEffect(() => {
    if (sessionTimeEventId && sessionTime) {
    updateEventAmount({
      id: sessionTimeEventId,
      event: "SESSION_TIME",
      event_type: "SESSION",
      event_amount: sessionTime,
      event_date: new Date(),
      pitch_page_id: page?.pitch_page_id,
      contact_id: investorId,
    })
  }
  }, [sessionTime])

  useEffect(() => {
    if (totalNumberOfSections === sectionsViewed.length) {
      if (!fullPageWasViewed) {
        sendAnalytics({
          event: "VIEWED_FULL_PAGE",
          event_type: "SESSION",
          event_amount: 1,
          event_date: new Date(),
          contact_id: investorId,
          pitch_page_id: page.pitch_page_id,
        });
      }
    setFullPageWasViewed(true)
    }
  }, [sectionsViewed]);
  
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const hero = page.sections?.find((x) => x.name === "company_hero");

  // create intersection observer
  const [isIntersecting, setIntersecting] = useState("");

  let options = {
    threshold: [0.55, 0.6, 0.65, 0.7, 0.75, 0.8, 0.85, 0.9, 0.95, 1],
  };

  function useOnScreen(ref, key) {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIntersecting(key);
      } else {
        setIntersecting("");
      }
    }, options);

    useEffect(() => {
      observer.observe(ref.current);
      // Remove the observer as soon as the component is unmounted
      return () => {
        observer.disconnect();
      };
      //eslint-disable-next-line
    }, []);

    return isIntersecting;
  }

  return (
    <div className="landing">
      <Helmet>
        <title>{page.name} - PitchPages</title>
        <meta name="theme-color" content={page.primary_color} />
        <meta
          name="description"
          content={hero ? hero.header : `View ${page.name}'s pitch page.`}
        />
        <meta property="og:title" content={page.name} />
        <meta
          property="og:description"
          content={hero ? hero.header : `View ${page.name}'s pitch page.`}
        />
        <meta property="og:url" content={window.location.origin} />
        <meta
          property="og:image"
          content={`${window.location.origin}${page.logo}`}
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={page.name} />
        <meta
          name="twitter:description"
          content={hero ? hero.header : `View ${page.name}'s pitch page.`}
        />
        <meta
          name="twitter:image"
          content={`${window.location.origin}${page.logo}`}
        />
      </Helmet>

      <Container>
        <MobileNav page={page} />
        <AuthNavBar page={page} investorId={investorId} />
      </Container>
      {page.sections
        ?.sort((a, b) => {
          return a.position - b.position;
        })
        .map((section, index) => {
          // new components for page templates are added here
          switch (section.name) {
            case "company_hero":
              return (
                <HeroVertical
                  info={section}
                  key={index}
                  page={page}
                  investorId={investorId}
                  currentSec={isIntersecting}
                  sectionsViewed={sectionsViewed}
                  setSectionsViewed={setSectionsViewed}
                  totalNumberOfSections={totalNumberOfSections}
                />
              );

            case "big_idea":
              return (
                <BigIdea
                  info={section}
                  key={index}
                  page={page}
                  checkScreen={useOnScreen}
                />
              );

            case "product_explanation":
              return (
                <Product
                  info={section}
                  key={index}
                  page={page}
                  checkScreen={useOnScreen}
                />
              );

            case "product_explanation_expanded":
              return (
                <ProductExpanded
                  info={section}
                  key={index}
                  page={page}
                  checkScreen={useOnScreen}
                />
              );

            case "competitor_landscape":
              return (
                <CompetitorLandscape
                  info={section}
                  key={index}
                  page={page}
                  checkScreen={useOnScreen}
                />
              );

            case "container_cards":
              if (
                !_.isNil(section.is_carousel) &&
                section.is_carousel === "true"
              ) {
                return (
                  <CarouselCards
                    info={section}
                    key={index}
                    page={page}
                    checkScreen={useOnScreen}
                  />
                );
              } else {
                return (
                  <Cards
                    info={section}
                    key={index}
                    page={page}
                    checkScreen={useOnScreen}
                  />
                );
              }

            case "team_members":
              if (
                !_.isNil(section.is_carousel) &&
                section.is_carousel === "true"
              ) {
                return (
                  <TeamMembersCarousel
                    info={section}
                    key={index}
                    page={page}
                    checkScreen={useOnScreen}
                    investorId={investorId}
                  />
                );
              } else {
                return (
                  <TeamMembers
                    info={section}
                    key={index}
                    page={page}
                    checkScreen={useOnScreen}
                    investorId={investorId}
                  />
                );
              }

            case "final_message_with_cta":
              return (
                <FinalCTA
                  info={section}
                  key={index}
                  page={page}
                  checkScreen={useOnScreen}
                />
              );

            case "projection":
              if (!_.isNil(section.image)) {
                return (
                  <ImageProjection
                    info={section}
                    key={index}
                    page={page}
                    checkScreen={useOnScreen}
                  />
                );
              } else {
                return (
                  <Projections
                    info={section}
                    key={index}
                    page={page}
                    checkScreen={useOnScreen}
                  />
                );
              }

            case "investment":
              return (
                <Investments
                  info={section}
                  key={index}
                  page={page}
                  checkScreen={useOnScreen}
                />
              );

            case "business_model":
              if (
                !_.isNil(section.is_carousel) &&
                section.is_carousel === "true"
              ) {
                return (
                  <BusinessModelCarousel
                    info={section}
                    key={index}
                    page={page}
                    checkScreen={useOnScreen}
                  />
                );
              } else {
                return (
                  <BusinessModel
                    info={section}
                    key={index}
                    page={page}
                    checkScreen={useOnScreen}
                  />
                );
              }

            case "downloads":
              return (
                <Downloads
                  info={section}
                  key={index}
                  page={page}
                  checkScreen={useOnScreen}
                  investorId={investorId}
                />
              );

            case "research":
              return (
                <Research
                  info={section}
                  key={index}
                  page={page}
                  checkScreen={useOnScreen}
                />
              );

            default:
              return "";
          }
        })}
      <Container>
        <section id="contact" className="signup">
          <div className="heading-container text-center">
            <label
              className="pre-heading"
              style={{ color: props.page.secondary_color }}
            >
              CONTACT US
            </label>
            <h2 className="heading">
              Want to learn more about our company?
              <br /> Click below
            </h2>
            <a
              href={`mailto:${props.page.founder_email}`}
              className="btn btn-custom"
              style={{
                backgroundColor: props.page.primary_color,
                boxShadow:
                  "0 0 15px " +
                  hexRgb(`${props.page.primary_color}66`, { format: "css" }),
              }}
              onClick={() =>
                sendAnalytics({
                  event: "CONTACT_US_CLICK",
                  event_type: "CONTACT",
                  event_amount: 1,
                  event_date: new Date(),
                  contact_id: investorId,
                  pitch_page_id: page.pitch_page_id,
                })
              }
            >
              Email Us
            </a>
          </div>
        </section>
      </Container>
      <Container>
        <section className={"powered-by text-center"} id="powered-by">
          <div className="mb-2">
            <span>
              Powered By{" "}
              <a href="https://pitchpages.io/">
                <img src={logo} width="150px" alt="PitchPages" />
              </a>
            </span>
          </div>
          {props.page && props.page.pitch_page_updated_at && (
            <span className="text-tiny">
              Last updated at:{" "}
              {format(
                utcToZonedTime(new Date(props.page.pitch_page_updated_at), tz),
                "MM/dd/yyyy"
              )}
            </span>
          )}
        </section>
      </Container>
    </div>
  );
};

export default BasicTemplate;
